import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { isCacheFresh } from '../utils/cache-policy';

export interface ImageCacheCreateOptions {
  url: string;
  title?: string;
  key?: string;
  forceRefresh?: boolean;
  expectedSize?: number;
  download?: () => Promise<Blob>;
}

export interface CachedImageRecord {
  key: string;
  title: string;
  sourceUrl: string;
  fileName: string;
  path: string;
  uri: string;
  localUrl: string;
  mimeType: string;
  size: number;
  createdAt: number;
  updatedAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class ImageCacheService {
  private readonly cacheFolder = 'image-cache';
  private readonly indexFile = `${this.cacheFolder}/index.json`;
  private readonly webCacheName = 'construfacil-image-cache-v1';
  private readonly webCachePath = '/__construfacil_image_cache__/';
  private readonly recordsByKey = new Map<string, CachedImageRecord>();
  private readonly keyByUrl = new Map<string, string>();
  private readonly webObjectUrls = new Map<string, Set<string>>();
  private indexLoaded = false;

  constructor(private http: HttpClient) { }

  private get canUseFileCache(): boolean {
    return Capacitor.isNativePlatform();
  }

  async create(options: ImageCacheCreateOptions): Promise<CachedImageRecord | null> {
    return this.cacheImageFromUrl(options);
  }

  async save(url: string, title = 'imagem', key?: string): Promise<string> {
    const record = await this.cacheImageFromUrl({ url, title, key });
    return record?.localUrl || url || '';
  }

  async cacheImageFromUrl(options: ImageCacheCreateOptions): Promise<CachedImageRecord | null> {
    const sourceUrl = String(options?.url || '').trim();
    if (!sourceUrl) {
      return null;
    }

    await this.loadIndex();
    const key = this.getCacheKey(sourceUrl, options.key || options.title);
    const cachedByUrl = this.getRecordByUrl(sourceUrl);
    const cachedByKey = this.recordsByKey.get(key);
    const cached = cachedByUrl || cachedByKey || null;
    // URLs assinadas mudam a query a cada renovação, mas continuam apontando para o mesmo objeto.
    const cachedMatchesUrl = !!cached && this.isSameSourceObject(cached.sourceUrl, sourceUrl);
    const cachedMatchesSize = !options.expectedSize || Number(cached?.size) === Number(options.expectedSize);

    if (cached && cachedMatchesUrl && cachedMatchesSize && !options.forceRefresh
      && isCacheFresh(cached.updatedAt) && await this.hasLocalFile(cached)) {
      return this.normalizeRecordUrl(cached);
    }

    if (!this.canUseFileCache && !options.forceRefresh) {
      const webCached = await this.readWebCachedImage(key, sourceUrl, options.title, options.expectedSize);
      if (webCached) {
        return webCached;
      }
    }

    // No navegador, imagens de outro domínio podem ser exibidas normalmente por <img>,
    // mas não podem ser baixadas por XHR quando o servidor legado não libera CORS.
    // O cache persistente continua ativo no aplicativo nativo e para URLs da mesma origem.
    if (!options.download && !this.canUseFileCache && this.isCrossOriginHttpUrl(sourceUrl)) {
      return cached && cachedMatchesUrl && cachedMatchesSize ? this.normalizeRecordUrl(cached) : null;
    }

    const response = options.download
      ? await this.downloadBlob(options.download, sourceUrl)
      : await this.downloadImage(sourceUrl);
    if (!response) {
      return cached && cachedMatchesUrl && cachedMatchesSize ? this.normalizeRecordUrl(cached) : null;
    }

    const mimeType = response.mimeType || this.getMimeTypeFromUrl(sourceUrl);
    const fileName = this.buildFileName(key, options.title || cached?.title || sourceUrl, mimeType);
    const path = `${this.cacheFolder}/${fileName}`;
    const data = this.arrayBufferToBase64(response.buffer);

    if (!this.canUseFileCache) {
      await this.saveWebCachedImage(key, sourceUrl, response.buffer, mimeType, options.title || cached?.title || 'imagem');
      const localUrl = this.createWebObjectUrl(key, response.buffer, mimeType);
      const record = this.buildRecord({
        key,
        title: options.title || cached?.title || this.titleFromUrl(sourceUrl),
        sourceUrl,
        fileName,
        path,
        uri: localUrl,
        localUrl,
        mimeType,
        size: response.buffer.byteLength,
        createdAt: cached?.createdAt || Date.now(),
      });
      this.setRecord(record);
      return record;
    }

    await this.ensureCacheFolder();
    await Filesystem.writeFile({
      path,
      data,
      directory: Directory.Data,
    });

    const stat = await Filesystem.stat({
      path,
      directory: Directory.Data,
    });

    if (cachedByKey?.path && cachedByKey.path !== path) {
      try {
        await this.deleteLocalFile(cachedByKey.path);
      } catch (error) {
        // O arquivo antigo pode já ter sido limpo pelo sistema.
      }
    }

    const record = this.buildRecord({
      key,
      title: options.title || cached?.title || this.titleFromUrl(sourceUrl),
      sourceUrl,
      fileName,
      path,
      uri: stat.uri,
      localUrl: Capacitor.convertFileSrc(stat.uri),
      mimeType,
      size: response.buffer.byteLength,
      createdAt: cached?.createdAt || Date.now(),
    });

    this.setRecord(record);
    await this.saveIndex();
    return record;
  }

  async read(keyOrUrl: string): Promise<CachedImageRecord | null> {
    await this.loadIndex();
    const record = this.getRecord(keyOrUrl);
    if (!record || !isCacheFresh(record.updatedAt) || !await this.hasLocalFile(record)) {
      return null;
    }

    return this.normalizeRecordUrl(record);
  }

  async getByKey(key: string): Promise<CachedImageRecord | null> {
    return this.read(this.slugify(key));
  }

  async update(keyOrUrl: string, changes: Partial<Pick<CachedImageRecord, 'title' | 'sourceUrl'>>): Promise<CachedImageRecord | null> {
    await this.loadIndex();
    const record = this.getRecord(keyOrUrl);
    if (!record) {
      return null;
    }

    if (changes.sourceUrl && changes.sourceUrl !== record.sourceUrl) {
      return this.cacheImageFromUrl({
        url: changes.sourceUrl,
        title: changes.title || record.title,
        key: record.key,
        forceRefresh: true,
      });
    }

    const updated = this.buildRecord({
      ...record,
      title: changes.title || record.title,
      createdAt: record.createdAt,
    });
    this.setRecord(updated);
    await this.saveIndex();
    return updated;
  }

  async delete(keyOrUrl: string): Promise<boolean> {
    await this.loadIndex();
    const record = this.getRecord(keyOrUrl);
    if (!record) {
      if (this.canUseWebCache()) {
        const key = this.slugify(keyOrUrl);
        this.revokeWebObjectUrl(key);
        await this.deleteWebCachedImage(key);
        return true;
      }

      return false;
    }

    this.recordsByKey.delete(record.key);
    this.keyByUrl.delete(record.sourceUrl);
    this.revokeWebObjectUrl(record.key);

    if (this.canUseFileCache) {
      try {
        await this.deleteLocalFile(record.path);
      } catch (error) {
        // Se o arquivo já não existe, removemos apenas o registro do índice.
      }
      await this.saveIndex();
    } else {
      await this.deleteWebCachedImage(record.key);
    }

    return true;
  }

  async remove(keyOrUrl: string): Promise<boolean> {
    return this.delete(keyOrUrl);
  }

  async list(): Promise<CachedImageRecord[]> {
    await this.loadIndex();
    return Array.from(this.recordsByKey.values()).map((record) => this.normalizeRecordUrl(record));
  }

  async clear(): Promise<void> {
    await this.loadIndex();

    for (const record of Array.from(this.recordsByKey.values())) {
      await this.delete(record.key);
    }

    this.recordsByKey.clear();
    this.keyByUrl.clear();
    this.webObjectUrls.forEach((urls) => {
      urls.forEach((url) => URL.revokeObjectURL(url));
    });
    this.webObjectUrls.clear();

    if (this.canUseFileCache) {
      await this.saveIndex();
    } else if (this.canUseWebCache()) {
      await caches.delete(this.webCacheName);
    }
  }

  async getLocalUrl(keyOrUrl: string): Promise<string> {
    const record = await this.read(keyOrUrl);
    return record?.localUrl || '';
  }

  async checkAndCacheImages(imagens: any[], etapa: any): Promise<void> {
    const imagePromises = (imagens || []).map(async (imagem) => {
      const imagemUrl = imagem?.imagem_url || imagem?.url || '';
      const title = imagem?.titulo || imagem?.nome || imagem?.imagem_id || imagemUrl;
      const cached = await this.cacheImage(imagemUrl, String(title));
      return {
        url: imagemUrl,
        cached: cached || imagemUrl,
      };
    });

    etapa.cachedImages = await Promise.all(imagePromises);
  }

  async checkAndCacheReportImages(
    imagens: any[],
    grupo: any,
    userId: number,
    obraId: number,
    download: (imagem: any) => Promise<Blob>
  ): Promise<void> {
    const imagePromises = (imagens || []).map(async (imagem) => {
      const imagemUrl = String(imagem?.imagem_url || '').trim();
      const imagemId = Number(imagem?.imagem_id || 0);
      const cacheKey = this.reportImageKey(userId, obraId, imagemId);
      const record = await this.cacheImageFromUrl({
        url: imagemUrl,
        title: `relatorio-${obraId}-${imagemId}`,
        key: cacheKey,
        expectedSize: Math.max(0, Number(imagem?.size) || 0),
        download: () => download(imagem)
      });

      return {
        url: imagemUrl,
        cached: record?.localUrl || imagemUrl,
        cacheKey
      };
    });

    grupo.cachedImages = await Promise.all(imagePromises);
  }

  async clearReportImage(userId: number, obraId: number, imagemId: number): Promise<void> {
    await this.delete(this.reportImageKey(userId, obraId, imagemId));
  }

  async reconcileReportImages(userId: number, obraId: number, imageIds: number[]): Promise<void> {
    await this.loadIndex();
    const prefix = this.slugify(`usuario-${userId}-relatorio-${obraId}-imagem-`);
    const validKeys = new Set(imageIds.map((imagemId) => this.reportImageKey(userId, obraId, imagemId)));

    for (const key of Array.from(this.recordsByKey.keys())) {
      if (key.startsWith(prefix) && !validKeys.has(key)) {
        await this.delete(key);
      }
    }

    if (!this.canUseWebCache()) {
      return;
    }

    try {
      const cache = await caches.open(this.webCacheName);
      const requests = await cache.keys();
      for (const request of requests) {
        const pathname = new URL(request.url).pathname;
        if (!pathname.startsWith(this.webCachePath)) continue;
        const key = decodeURIComponent(pathname.slice(this.webCachePath.length));
        if (key.startsWith(prefix) && !validKeys.has(key)) {
          this.revokeWebObjectUrl(key);
          await cache.delete(request);
        }
      }
    } catch (error) {
      // A reconciliação é retomada no próximo acesso se o navegador bloquear o Cache Storage.
    }
  }

  async getCachedImage(url: string): Promise<string | null> {
    const record = await this.read(url);
    return record?.localUrl || null;
  }

  async cacheImage(url: string, title?: string): Promise<string> {
    const sourceUrl = String(url || '').trim();
    if (!sourceUrl) {
      return '';
    }

    const record = await this.cacheImageFromUrl({ url: sourceUrl, title });
    return record?.localUrl || sourceUrl;
  }

  async clearOldCache(): Promise<void> {
    await this.loadIndex();
    const expiredKeys = Array.from(this.recordsByKey.values())
      .filter((record) => !isCacheFresh(record.updatedAt))
      .map((record) => record.key);
    await Promise.all(expiredKeys.map((key) => this.delete(key)));
  }

  cleanupCachePeriodically(): void {
    void this.clearOldCache();
  }

  async clearCacheForImage(urlOrKey: string): Promise<void> {
    await this.delete(urlOrKey);
  }

  async fileExists(urlOrKey: string): Promise<boolean> {
    const record = await this.read(urlOrKey);
    return !!record;
  }

  private async loadIndex(): Promise<void> {
    if (this.indexLoaded) {
      return;
    }

    this.indexLoaded = true;
    this.recordsByKey.clear();
    this.keyByUrl.clear();

    if (!this.canUseFileCache) {
      return;
    }

    try {
      await this.ensureCacheFolder();
      const file = await Filesystem.readFile({
        path: this.indexFile,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
      const records = JSON.parse(String(file.data || '[]')) as CachedImageRecord[];
      records.forEach((record) => this.setRecord(this.normalizeRecordUrl(record)));
    } catch (error) {
      await this.saveIndex();
    }
  }

  private async saveIndex(): Promise<void> {
    if (!this.canUseFileCache) {
      return;
    }

    await this.ensureCacheFolder();
    await Filesystem.writeFile({
      path: this.indexFile,
      data: JSON.stringify(Array.from(this.recordsByKey.values())),
      directory: Directory.Data,
      encoding: Encoding.UTF8,
    });
  }

  private async ensureCacheFolder(): Promise<void> {
    if (!this.canUseFileCache) {
      return;
    }

    try {
      await Filesystem.mkdir({
        path: this.cacheFolder,
        directory: Directory.Data,
        recursive: true,
      });
    } catch (error) {
      // A pasta pode já existir.
    }
  }

  private async hasLocalFile(record: CachedImageRecord): Promise<boolean> {
    if (!this.canUseFileCache) {
      return this.webObjectUrls.has(record.key) || !!record.localUrl;
    }

    try {
      await Filesystem.stat({
        path: record.path,
        directory: Directory.Data,
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  private async deleteLocalFile(path: string): Promise<void> {
    if (!this.canUseFileCache || !path) {
      return;
    }

    await Filesystem.deleteFile({
      path,
      directory: Directory.Data,
    });
  }

  private async downloadImage(url: string): Promise<{ buffer: ArrayBuffer; mimeType: string } | null> {
    if (this.isLocalImage(url)) {
      return null;
    }

    try {
      const response = await this.http.get(url, {
        responseType: 'arraybuffer',
        observe: 'response',
      }).toPromise() as HttpResponse<ArrayBuffer>;
      return {
        buffer: response.body || new ArrayBuffer(0),
        mimeType: response.headers.get('content-type') || this.getMimeTypeFromUrl(url),
      };
    } catch (error) {
      return null;
    }
  }

  private async downloadBlob(
    download: () => Promise<Blob>,
    sourceUrl: string
  ): Promise<{ buffer: ArrayBuffer; mimeType: string } | null> {
    try {
      const blob = await download();
      if (!blob || blob.size <= 0 || String(blob.type || '').includes('application/json')) {
        return null;
      }
      return {
        buffer: await blob.arrayBuffer(),
        mimeType: blob.type || this.getMimeTypeFromUrl(sourceUrl)
      };
    } catch (error) {
      return null;
    }
  }

  private buildRecord(record: Omit<CachedImageRecord, 'updatedAt'> & { updatedAt?: number }): CachedImageRecord {
    const updatedAt = Date.now();
    return {
      ...record,
      createdAt: record.createdAt || updatedAt,
      updatedAt,
    };
  }

  private setRecord(record: CachedImageRecord): void {
    const previousRecord = this.recordsByKey.get(record.key);
    if (previousRecord?.sourceUrl && previousRecord.sourceUrl !== record.sourceUrl) {
      this.keyByUrl.delete(previousRecord.sourceUrl);
    }

    this.recordsByKey.set(record.key, record);
    if (record.sourceUrl) {
      this.keyByUrl.set(record.sourceUrl, record.key);
    }
  }

  private getRecord(keyOrUrl: string): CachedImageRecord | null {
    const value = String(keyOrUrl || '').trim();
    if (!value) {
      return null;
    }

    const key = this.keyByUrl.get(value) || value;
    return this.recordsByKey.get(key) || null;
  }

  private getRecordByUrl(url: string): CachedImageRecord | null {
    const key = this.keyByUrl.get(url);
    return key ? this.recordsByKey.get(key) || null : null;
  }

  private normalizeRecordUrl(record: CachedImageRecord): CachedImageRecord {
    if (this.canUseFileCache && record.uri) {
      return {
        ...record,
        localUrl: Capacitor.convertFileSrc(record.uri),
      };
    }

    return record;
  }

  private getCacheKey(url: string, keyOrTitle?: string): string {
    if (keyOrTitle) {
      return this.slugify(keyOrTitle);
    }

    return `imagem-${this.hash(url)}`;
  }

  private buildFileName(key: string, title: string, mimeType: string): string {
    const extension = this.getExtensionFromMimeType(mimeType);
    const safeTitle = this.slugify(title || 'imagem');
    return `${safeTitle}-${this.hash(key)}.${extension}`;
  }

  private titleFromUrl(url: string): string {
    const cleanUrl = String(url || '').split('?')[0];
    const name = cleanUrl.split('/').pop() || 'imagem';
    return name.replace(/\.[a-zA-Z0-9]+$/, '') || 'imagem';
  }

  private slugify(value: string): string {
    return String(value || 'imagem')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9_-]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase()
      .slice(0, 80) || 'imagem';
  }

  private hash(value: string): string {
    let hash = 0;
    const text = String(value || '');
    for (let index = 0; index < text.length; index++) {
      hash = ((hash << 5) - hash) + text.charCodeAt(index);
      hash |= 0;
    }
    return Math.abs(hash).toString(36);
  }

  private getMimeTypeFromUrl(url: string): string {
    const cleanUrl = String(url || '').split('?')[0].toLowerCase();
    if (cleanUrl.endsWith('.png')) {
      return 'image/png';
    }
    if (cleanUrl.endsWith('.webp')) {
      return 'image/webp';
    }
    if (cleanUrl.endsWith('.gif')) {
      return 'image/gif';
    }
    return 'image/jpeg';
  }

  private isSameSourceObject(currentUrl: string, nextUrl: string): boolean {
    return this.sourceObjectIdentity(currentUrl) === this.sourceObjectIdentity(nextUrl);
  }

  private sourceObjectIdentity(url: string): string {
    const value = String(url || '').trim();
    if (!value) {
      return '';
    }

    try {
      const parsed = new URL(value, window.location.origin);
      return `${parsed.origin}${parsed.pathname}`;
    } catch (error) {
      return value.split(/[?#]/)[0];
    }
  }

  private canUseWebCache(): boolean {
    return !this.canUseFileCache && typeof caches !== 'undefined';
  }

  private async readWebCachedImage(
    key: string,
    sourceUrl: string,
    title?: string,
    expectedSize?: number
  ): Promise<CachedImageRecord | null> {
    if (!this.canUseWebCache()) {
      return null;
    }

    try {
      const cache = await caches.open(this.webCacheName);
      const response = await cache.match(this.webCacheRequest(key));
      if (!response) {
        return null;
      }

      const cachedAt = Number(response.headers.get('x-construfacil-cached-at') || 0);
      const cachedSource = response.headers.get('x-construfacil-source') || '';
      if (!cachedAt || !isCacheFresh(cachedAt) || !this.isSameSourceObject(cachedSource, sourceUrl)) {
        await cache.delete(this.webCacheRequest(key));
        return null;
      }

      const blob = await response.blob();
      if (expectedSize && blob.size !== Number(expectedSize)) {
        await cache.delete(this.webCacheRequest(key));
        return null;
      }
      const mimeType = blob.type || response.headers.get('content-type') || this.getMimeTypeFromUrl(sourceUrl);
      const buffer = await blob.arrayBuffer();
      const localUrl = this.createWebObjectUrl(key, buffer, mimeType);
      const record = this.buildRecord({
        key,
        title: title || response.headers.get('x-construfacil-title') || this.titleFromUrl(sourceUrl),
        sourceUrl,
        fileName: this.buildFileName(key, title || 'imagem', mimeType),
        path: this.webCacheRequest(key),
        uri: localUrl,
        localUrl,
        mimeType,
        size: blob.size,
        createdAt: cachedAt,
      });

      this.setRecord(record);
      return record;
    } catch (error) {
      return null;
    }
  }

  private async saveWebCachedImage(
    key: string,
    sourceUrl: string,
    buffer: ArrayBuffer,
    mimeType: string,
    title: string
  ): Promise<void> {
    if (!this.canUseWebCache()) {
      return;
    }

    try {
      const cache = await caches.open(this.webCacheName);
      const headers = new Headers({
        'content-type': mimeType,
        'x-construfacil-cached-at': String(Date.now()),
        'x-construfacil-source': this.sourceObjectIdentity(sourceUrl),
        'x-construfacil-title': title,
      });
      await cache.put(this.webCacheRequest(key), new Response(buffer.slice(0), { headers }));
    } catch (error) {
      // O navegador pode bloquear o Cache Storage em modo privado.
    }
  }

  private async deleteWebCachedImage(key: string): Promise<void> {
    if (!this.canUseWebCache()) {
      return;
    }

    try {
      const cache = await caches.open(this.webCacheName);
      await cache.delete(this.webCacheRequest(key));
    } catch (error) {
      // A ausência do cache não impede a remoção do registro em memória.
    }
  }

  private webCacheRequest(key: string): string {
    return `${window.location.origin}${this.webCachePath}${encodeURIComponent(key)}`;
  }

  private reportImageKey(userId: number, obraId: number, imagemId: number): string {
    return this.slugify(`usuario-${Number(userId)}-relatorio-${Number(obraId)}-imagem-${Number(imagemId)}`);
  }

  private getExtensionFromMimeType(mimeType: string): string {
    const normalized = String(mimeType || '').toLowerCase();
    if (normalized.includes('png')) {
      return 'png';
    }
    if (normalized.includes('webp')) {
      return 'webp';
    }
    if (normalized.includes('gif')) {
      return 'gif';
    }
    return 'jpg';
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    for (let index = 0; index < bytes.byteLength; index++) {
      binary += String.fromCharCode(bytes[index]);
    }
    return btoa(binary);
  }

  private createWebObjectUrl(key: string, buffer: ArrayBuffer, mimeType: string): string {
    const blob = new Blob([buffer], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const urls = this.webObjectUrls.get(key) || new Set<string>();
    urls.add(url);
    this.webObjectUrls.set(key, urls);
    return url;
  }

  private revokeWebObjectUrl(key: string): void {
    const urls = this.webObjectUrls.get(key);
    if (urls) {
      urls.forEach((url) => URL.revokeObjectURL(url));
      this.webObjectUrls.delete(key);
    }
  }

  private isLocalImage(url: string): boolean {
    return ['data:image/', 'blob:', 'capacitor://', 'file://'].some((prefix) => String(url || '').startsWith(prefix));
  }

  private isCrossOriginHttpUrl(url: string): boolean {
    try {
      const parsed = new URL(String(url || ''), window.location.origin);
      return /^https?:$/i.test(parsed.protocol) && parsed.origin !== window.location.origin;
    } catch (error) {
      return false;
    }
  }
}
